/** What the .docx reader hands to the PDF writer: a document reduced to what is needed to draw it. */

export interface RunProps {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  /** "RRGGBB" */
  color?: string;
  sizePt?: number;
  /** Font name as Word wrote it ("Calibri"); mapped to the nearest built-in family when drawing. */
  font?: string;
  vert?: "sup" | "sub";
  /** "RRGGBB" highlight / shading behind the text. */
  background?: string;
  caps?: boolean;
  smallCaps?: boolean;
  hidden?: boolean;
}

export type Inline =
  | { t: "text"; text: string; p: RunProps; link?: string }
  | { t: "tab"; p: RunProps }
  | { t: "br"; p: RunProps }
  | { t: "pagebreak" }
  | { t: "field"; kind: "PAGE" | "NUMPAGES"; p: RunProps }
  | { t: "footnoteRef"; label: string; p: RunProps }
  | { t: "image"; data: Uint8Array; type: "png" | "jpg"; wPt: number; hPt: number; alt?: string };

export interface Border {
  /** "single", "double", "dashed", "dotted" … ("nil"/"none" never appear: they are left out). */
  style: string;
  sizePt: number;
  color: string;
}

export interface BorderSet {
  top?: Border;
  bottom?: Border;
  left?: Border;
  right?: Border;
}

export interface TabStop {
  posPt: number;
  align: "left" | "center" | "right" | "decimal";
}

export interface ParaProps {
  align: "left" | "center" | "right" | "justify";
  beforePt: number;
  afterPt: number;
  /** Line spacing: a multiple of single ("auto"), or points ("exact" / "atLeast"). */
  line: { value: number; rule: "auto" | "exact" | "atLeast" };
  leftPt: number;
  rightPt: number;
  /** First-line offset relative to leftPt (negative = hanging indent). */
  firstPt: number;
  keepNext: boolean;
  keepLines: boolean;
  pageBreakBefore: boolean;
  contextualSpacing: boolean;
  widowControl: boolean;
  shade?: string;
  borders: BorderSet;
  tabs: TabStop[];
}

export interface ListLabel {
  text: string;
  bullet: boolean;
  /** Properties of the label's run (bullet font, bold numbers …). */
  p: RunProps;
}

export interface Para {
  kind: "p";
  inlines: Inline[];
  props: ParaProps;
  /** Base run properties for the paragraph (style + defaults); also the size of an empty line. */
  base: RunProps;
  list?: ListLabel;
  /** The paragraph style's id (for "don't add space between paragraphs of the same style"). */
  styleId?: string;
  /** Word's "Title" / "Heading n" outline level, if any (for the PDF's bookmarks, later). */
  outline?: number;
}

export interface Cell {
  blocks: Block[];
  span: number;
  vMerge?: "restart" | "continue";
  shade?: string;
  borders: BorderSet;
  vAlign: "top" | "center" | "bottom";
  /** Cell-specific margins in points, when set. */
  margins?: { left?: number; right?: number; top?: number; bottom?: number };
}

export interface Row {
  cells: Cell[];
  header: boolean;
  heightPt?: number;
  heightRule?: "atLeast" | "exact";
  cantSplit: boolean;
}

export interface Table {
  kind: "tbl";
  rows: Row[];
  /** Column widths in points (Word's grid). */
  grid: number[];
  borders: BorderSet & { insideH?: Border; insideV?: Border };
  margins: { left: number; right: number; top: number; bottom: number };
  align: "left" | "center" | "right";
  indentPt: number;
  /** Preferred width: points, or a fraction of the text width when given as a percentage. */
  widthPt?: number;
  widthFraction?: number;
}

export type Block = Para | Table;

export interface Section {
  widthPt: number;
  heightPt: number;
  marginTop: number;
  marginRight: number;
  marginBottom: number;
  marginLeft: number;
  headerDist: number;
  footerDist: number;
  titlePg: boolean;
  breakType: "nextPage" | "continuous" | "evenPage" | "oddPage";
  columns: number;
  header?: Block[];
  headerFirst?: Block[];
  footer?: Block[];
  footerFirst?: Block[];
  blocks: Block[];
}

export interface DocxWarnings {
  /** Text boxes and shapes whose text is not included. */
  textBoxes: number;
  /** Pictures that could not be drawn (unsupported format). */
  pictures: number;
  /** Sections laid out in several columns, shown as one. */
  columnSections: number;
  /** Tracked deletions are left out; this counts documents with them. */
  charts: number;
}

export interface DocxDoc {
  sections: Section[];
  footnotes: { label: string; blocks: Block[] }[];
  title?: string;
  author?: string;
  warnings: DocxWarnings;
}

/** Plain text of blocks (for text-only output and script checks). */
export function blocksText(blocks: Block[]): string {
  const out: string[] = [];
  const para = (p: Para) => {
    let s = p.list ? p.list.text + " " : "";
    // "Page break before" starts a new page (nothing to do at the very top).
    if ((p as { props?: { pageBreakBefore?: boolean } }).props?.pageBreakBefore && out.length > 0) s = "\f" + s;
    for (const i of p.inlines) {
      if (i.t === "text") s += i.text;
      else if (i.t === "tab") s += "\t";
      else if (i.t === "br") s += "\n";
      else if (i.t === "pagebreak") s += "\f";
      else if (i.t === "footnoteRef") s += i.label;
    }
    out.push(s);
  };
  for (const b of blocks) {
    if (b.kind === "p") para(b);
    else
      for (const row of b.rows) {
        const cells = row.cells.map((c) => blocksText(c.blocks).replace(/\n+/g, " ").trim());
        out.push(cells.join("\t"));
      }
  }
  return out.join("\n");
}
